import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../utils/apiUtils';
import { Container, Card, Row, Col, Badge, Button, Alert, Table, Modal, Form } from 'react-bootstrap';
import { getItemPermissions, canToggleEditLock } from '../utils/permissionUtils';
import { useNotification } from '../hooks/useNotification';
import WorkPlanModal from './WorkPlanModal';

const WorkPlanView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workPlan, setWorkPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [docxLoading, setDocxLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [errorDecision, setErrorDecision] = useState('');
  const [showDecision, setShowDecision] = useState(false);
  const [decision, setDecision] = useState('Approved');
  const [remarks, setRemarks] = useState('');
  const [editLockLoading, setEditLockLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const { notification, showSuccess, showError, dismiss } = useNotification();

  // Toggle edit lock function
  const handleToggleEditLock = async () => {
    if (!workPlan) return;
    setEditLockLoading(true);
    try {
      const newLockState = !workPlan.edit_locked;
      const response = await apiClient.put(`${API_ENDPOINTS.WORK_PLANS}/${workPlan.work_plan_id}/edit-lock`, { edit_locked: newLockState });
      // Preserve planning_rows when updating - the edit-lock response doesn't include them
      const preservedPlanningRows = workPlan.planning_rows || [];
      if (response.data && response.data.workPlan) {
        setWorkPlan({
          ...response.data.workPlan,
          planning_rows: preservedPlanningRows
        });
      } else {
        setWorkPlan(prev => ({ 
          ...prev, 
          edit_locked: newLockState,
          planning_rows: preservedPlanningRows
        }));
      }
      setSuccessMessage(`Edit permissions ${newLockState ? 'locked' : 'unlocked'} successfully.`);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Failed to toggle edit lock:', err);
      showError(err.response?.data?.error || 'Failed to toggle edit lock');
    } finally {
      setEditLockLoading(false);
    }
  };

  useEffect(() => {
    const fetchWorkPlan = async () => {
      try {
        const { data } = await apiClient.get(`${API_ENDPOINTS.WORK_PLANS}/${id}`);
        setWorkPlan(data);
      } catch (err) {
        console.error('Failed to load work plan', err);
        setError('Failed to load work plan');
      } finally {
        setLoading(false);
      }
    };
    fetchWorkPlan();
  }, [id]);

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Checked and Verified by Extension Head':
      case 'Approved by VC-RDES':
        return 'success';
      case 'Rejected by Extension Head':
      case 'Rejected by VC-RDES':
        return 'danger';
      case 'Pending':
      default:
        return 'warning';
    }
  };

  const getApprovalChecklist = (wp) => {
    const s = wp.status;

    const ehDone = ['Checked and Verified by Extension Head','Approved by VC-RDES','Rejected by VC-RDES','On-Going','Completed'].includes(s);
    const vcrdesDone = ['Approved by VC-RDES','On-Going','Completed'].includes(s);

    const steps = [
      {
        key: 'eh',
        label: ehDone ? 'Checked and Verified by Extension Head' : 'To be Checked and Verified by Extension Head',
        done: ehDone
      },
      {
        key: 'vcrdes',
        label: vcrdesDone ? 'Approved by VC-RDES' : 'To be Approved by VCRDES',
        done: vcrdesDone
      }
    ];

    return steps;
  };

  const getNextApprovalStep = (wp) => {
    const s = wp.status;
    if (['Rejected by Extension Head','Rejected by VC-RDES','Completed','On-Going'].includes(s)) return null;
    if (s === 'Pending') return 'To be Checked and Verified by Extension Head';
    if (s === 'Checked and Verified by Extension Head') return 'To be Approved by VCRDES';
    if (s === 'Approved by VC-RDES') return null;
    return null;
  };

  const canCurrentUserDecide = (wp) => {
    if (!user) return false;
    const role = user.role;
    const s = wp.status;
    if (role === 'Extension Head') return s === 'Pending';
    if (role === 'Vice Chancellor for Research, Development and Extension Services') return s === 'Checked and Verified by Extension Head';
    return false;
  };

  const submitDecision = async (decisionArg) => {
    try {
      setDecisionLoading(true);
      setErrorDecision('');
      await apiClient.post(`${API_ENDPOINTS.WORK_PLANS}/${id}/decision`, {
        decision: decisionArg,
        remarks: remarks || null
      });
      
      // Show success message before navigating
      if (decisionArg === 'Approved') {
        showSuccess('Work plan approved successfully!');
      } else if (decisionArg === 'Rejected') {
        showSuccess('Work plan rejected successfully!');
      }
      
      // Small delay to show success message before navigating
      setTimeout(() => {
        navigate('/plan');
      }, 500);
      
      setShowDecision(false);
      setRemarks('');
    } catch (e) {
      console.error('Decision failed', e);
      const errorMessage = e.response?.data?.error || 'Failed to submit decision';
      setErrorDecision(errorMessage);
      showError(errorMessage);
    } finally {
      setDecisionLoading(false);
    }
  };

  // Handle reproposing a rejected work plan
  const handleRepropose = async () => {
    try {
      setError('');
      const { data } = await apiClient.put(`${API_ENDPOINTS.WORK_PLANS}/${id}/repropose`);
      setWorkPlan(data);
      // Show success message or navigate back to list
      navigate('/plan');
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to repropose work plan. Please try again.';
      setError(errorMsg);
    }
  };

  const handleGenerateTemplate = async () => {
    setTemplateLoading(true);
    setError('');

    try {
      const templateResponse = await apiClient.post(`${API_ENDPOINTS.WORK_PLANS}/generate-template`, {
        work_plan_id: id,
        timestamp: new Date().toISOString()
      });

      // Create the print HTML with the template content and auto-print script
      const html = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Extension Work Plan</title>
          <style>
            * { box-sizing: border-box; }
            html, body { margin: 0; padding: 0; background: #fff; }
            @page { size: A4 landscape; margin: 12mm; }
            #template-container { 
              width: 100%; 
              border: 2px solid #000; 
              background: #fff; 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
              page-break-inside: avoid;
            }
            table { width: 100%; border-collapse: collapse; }
            td, th { border: 1px solid #000; padding: 6px 8px; vertical-align: top; }
            .header-logo { width: 60px; height: 60px; }
            .title { font-weight: bold; text-align: center; font-size: 16px; }
            .month-header { text-align: center; font-size: 10px; }
            .month-cell { text-align: center; width: 30px; }
            .objectives-cell { width: 25%; }
            .output-cell { width: 25%; }
            .activities-cell { width: 25%; }
            .months-cell { width: 25%; }
            .add-rows-note { font-style: italic; font-size: 10px; }
          </style>
        </head>
        <body>
          ${templateResponse.data.htmlContent}
          <script>
            window.onload = function() {
              setTimeout(function(){ window.print(); window.close(); }, 50);
            };
          <\/script>
        </body>
      </html>`;
      
      const printWindow = window.open('', '_blank', 'width=1200,height=900');
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

    } catch (err) {
      console.error('Error generating template:', err);
      setError('Failed to generate template. Please try again.');
    } finally {
      setTemplateLoading(false);
    }
  };

  // Handle DOCX download for Work Plan
  const handleDownloadDocx = async () => {
    try {
      setError('');
      setDocxLoading(true);
      
      // Generate the work plan DOCX
      const response = await axios.post(`${API_ENDPOINTS.WORK_PLANS}/generate-docx`, {
        work_plan_id: id
      }, {
        responseType: 'blob' // Important for file download
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Work_Plan_${id}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess('Work Plan DOCX downloaded successfully!');
    } catch (error) {
      console.error('Error generating DOCX work plan:', error);
      const errorMessage = error.response?.data?.error || 'Failed to generate DOCX work plan. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setDocxLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100">
        <Container className="py-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading work plan...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light min-vh-100">
        <Container className="py-4">
          <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>
        </Container>
      </div>
    );
  }

  if (!workPlan) return null;

  return (
    <div className="bg-light min-vh-100">
      <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Work Plan Details</h2>
          </div>
          <div className="d-flex gap-2">
            {canCurrentUserDecide(workPlan) && (
              <>
                <Button 
                  size="sm" 
                  variant="success" 
                  style={{ minWidth: '40px', height: '32px' }} 
                  onClick={() => { setDecision('Approved'); setShowDecision(true); }}
                  title="Approve"
                >
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button 
                  size="sm" 
                  variant="danger" 
                  style={{ minWidth: '40px', height: '32px' }} 
                  onClick={() => { setDecision('Rejected'); setShowDecision(true); }}
                  title="Reject"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </>
            )}
            {/* Edit button - using new permission system */}
            {user && workPlan && (() => {
              const permissions = getItemPermissions(user, workPlan, 'workplan');
              return permissions.canEdit;
            })() && (
              <Button 
                variant="outline-primary" 
                size="sm"
                style={{ minWidth: '40px', height: '32px' }}
                onClick={() => setShowEditModal(true)}
                title="Edit Work Plan"
              >
                <i className="bi bi-pencil"></i>
              </Button>
            )}

            {/* Lock/Unlock Edit Permission button - for elevated roles */}
            {user && workPlan && canToggleEditLock(user) && (
              <Button 
                variant={workPlan.edit_locked ? "warning" : "outline-secondary"}
                size="sm"
                style={{ minWidth: '40px', height: '32px' }}
                onClick={handleToggleEditLock}
                disabled={editLockLoading}
                title={workPlan.edit_locked ? "Unlock Edit Permission" : "Lock Edit Permission"}
              >
                <i className={`bi ${workPlan.edit_locked ? 'bi-lock-fill' : 'bi-unlock'}`}></i>
              </Button>
            )}
            
            {/* Repropose button - visible to creator on any rejected status */}
            {user && ['Rejected by Extension Head', 'Rejected by VC-RDES'].includes(workPlan.status) && workPlan.created_by === user.user_id && (
              <Button 
                size="sm" 
                variant="warning" 
                style={{ minWidth: '40px', height: '32px' }}
                onClick={handleRepropose}
                title="Resubmit this work plan for approval"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </Button>
            )}
            
            <Button 
              variant="outline-primary" 
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={handleGenerateTemplate}
              disabled={templateLoading}
              title="Print Work Plan (HTML)"
            >
              {templateLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-printer"></i>
              )}
            </Button>
            <Button 
              variant="outline-primary"
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={handleDownloadDocx}
              disabled={docxLoading}
              title="Download as Word document (DOCX)"
            >
              {docxLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-file-earmark-word"></i>
              )}
            </Button>
            
            <Button 
              variant="secondary" 
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={() => navigate('/plan')}
              title="Back to Work Plans"
            >
              <i className="bi bi-arrow-left"></i>
            </Button>
          </div>
        </div>

        {/* Notification Alert */}

        <Card className="shadow-sm mb-4">
          <Card.Body>
            <Row className="mb-3">
              <Col md={3}><strong>Title</strong></Col>
              <Col md={9}>{workPlan.title}</Col>
            </Row>
            <Row className="mb-1">
              <Col md={3}><strong>Status</strong></Col>
              <Col md={9}><Badge bg={getStatusVariant(workPlan.status)}>{workPlan.status}</Badge></Col>
            </Row>
            {(() => { const next = getNextApprovalStep(workPlan); return next ? (
              <Row className="mb-1">
                <Col md={3}></Col>
                <Col md={9} className="text-muted small d-flex align-items-center" style={{ fontStyle: 'italic' }}>
                  <i className="bi bi-eye me-1" aria-hidden="true"></i>
                  <span>{next}</span>
                </Col>
              </Row>
            ) : null; })()}

            {/* Approval Checklist (inline like ProjectView) */}
            <Row className="mb-2">
              <Col md={3}></Col>
              <Col md={9}>
                {getApprovalChecklist(workPlan).map(step => (
                  <div key={step.key} className="d-flex align-items-center text-muted small">
                    <i className={`bi ${step.done ? 'bi-check-square-fill text-success' : 'bi-square'} me-1`} aria-hidden="true"></i>
                    <span>{step.label}</span>
                  </div>
                ))}
                {errorDecision && (
                  <Alert variant="danger" className="mt-2 py-1 px-2" onClose={() => setErrorDecision('')} dismissible>{errorDecision}</Alert>
                )}
              </Col>
            </Row>

            {/* Remarks for approved or rejected status */}
            {workPlan.remarks && (
              <Row className="mb-3">
                <Col md={12}>
                  {(['Rejected by Extension Head','Rejected by VC-RDES'].includes(workPlan.status)) ? (
                    <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #dc3545' }}>
                      <div className="d-flex align-items-start">
                        <i className="bi bi-exclamation-triangle-fill text-danger me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                        <div className="w-100">
                          <strong className="text-danger d-block">
                            <i className="bi bi-exclamation-circle-fill me-1"></i>Remarks:
                          </strong>
                          <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{workPlan.remarks}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #28a745' }}>
                      <div className="d-flex align-items-start">
                        <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                        <div className="w-100">
                          <strong className="text-success d-block">
                            <i className="bi bi-info-circle-fill me-1"></i>Remarks:
                          </strong>
                          <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{workPlan.remarks}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            )}
            <Row className="mb-3">
              <Col md={3}><strong>Total Duration</strong></Col>
              <Col md={9}>{workPlan.total_duration}</Col>
            </Row>
            <Row className="mb-3">
              <Col md={3}><strong>Planned Start</strong></Col>
              <Col md={9}>{new Date(workPlan.planned_start).toLocaleDateString()}</Col>
            </Row>
            <Row className="mb-3">
              <Col md={3}><strong>Planned End</strong></Col>
              <Col md={9}>{new Date(workPlan.planned_end).toLocaleDateString()}</Col>
            </Row>
            <Row className="mb-3">
              <Col md={3}><strong>Created By</strong></Col>
              <Col md={9}>{workPlan.created_by_name} ({workPlan.created_by_department})</Col>
            </Row>
            <div className="table-responsive">
              <Table bordered hover>
                <thead className="table-light">
                  <tr>
                    <th className="text-center" style={{ width: '60%' }}>Objectives</th>
                    <th className="text-center" style={{ width: '60%' }}>Expected Output</th>
                    <th className="text-center" style={{ width: '60%' }}>Activities</th>
                    <th colSpan="12" className="text-center">Y1</th>
                  </tr>
                  <tr>
                    <th></th>
                    <th></th>
                    <th></th>
                    {['M1','M2','M3','M4','M5','M6','M7','M8','M9','M10','M11','M12'].map(m => (
                      <th key={m} className="text-center small" style={{ width: '0.1%' }}>{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(workPlan.planning_rows || []).map((row, idx) => (
                    <tr key={idx}>
                      <td style={{whiteSpace:'pre-wrap'}}>{row.objectives}</td>
                      <td style={{whiteSpace:'pre-wrap'}}>{row.expected_output}</td>
                      <td style={{whiteSpace:'pre-wrap'}}>{row.activities}</td>
                      {['M1','M2','M3','M4','M5','M6','M7','M8','M9','M10','M11','M12'].map(m => (
                        <td 
                          key={m} 
                          className="text-center"
                          style={{ 
                            backgroundColor: row.months?.[m] ? '#dc3545' : 'transparent',
                            border: '1px solid #dee2e6',
                            minWidth: '40px',
                            height: '40px'
                          }}
                        >
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        
      </Container>

      {/* Approve/Reject Modal */}
      <Modal show={showDecision} onHide={() => setShowDecision(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{decision === 'Approved' ? 'Approve Work Plan' : 'Reject Work Plan'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {decision === 'Approved' && (
            <>
              <div className="text-center py-3">
                <i className="bi bi-question-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3 mb-2">Confirm Work Plan Approval</h5>
                <p className="text-muted">Are you sure you want to approve this work plan?</p>
              </div>
              <Form.Group>
                <Form.Label>Remarks (optional)</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={remarks} 
                  onChange={(e) => setRemarks(e.target.value)} 
                  placeholder="Add any additional comments or notes..."
                />
              </Form.Group>
            </>
          )}
          {decision === 'Rejected' && (
            <>
              <div className="text-center py-3">
                <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3 mb-2">Confirm Work Plan Rejection</h5>
                <p className="text-muted">Are you sure you want to reject this work plan?</p>
              </div>
              <Form.Group>
                <Form.Label>Remarks (optional)</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={remarks} 
                  onChange={(e) => setRemarks(e.target.value)} 
                  placeholder="Please provide a reason for rejection..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDecision(false)}>Cancel</Button>
          <Button 
            variant={decision === 'Approved' ? 'success' : 'danger'} 
            onClick={() => submitDecision(decision)}
          >
            {decision === 'Approved' ? 'Approve' : 'Reject'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <h5 className="mb-0">{successMessage}</h5>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setShowSuccessModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Work Plan Modal */}
      <WorkPlanModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        workPlan={workPlan}
        onSuccess={() => {
          // Reload work plan data after successful edit
          const fetchWorkPlan = async () => {
            try {
              const { data } = await apiClient.get(`${API_ENDPOINTS.WORK_PLANS}/${id}`);
              setWorkPlan(data);
              showSuccess('Work plan updated successfully!');
            } catch (err) {
              console.error('Failed to reload work plan', err);
            }
          };
          fetchWorkPlan();
          setShowEditModal(false);
        }}
      />
    </div>
  );
};

export default WorkPlanView;


